/**
 * Google Pay loader + environment.
 *
 * Shared by CheckoutInfo (step 1 express button) and CheckoutPayment (step 3)
 * so the two cannot drift — same reason core/applePay.ts exists.
 *
 * BRAND GUIDELINES (developers.google.com/pay/api/web/guides/brand-guidelines):
 * the Google Pay button must come from PaymentsClient.createButton() — "Don't
 * create your own Google Pay buttons or alter the font, color, button radius,
 * or padding" — and a payment-method list must show the official Google Pay
 * mark (assets/img/google-pay-mark.svg, from Google's Google-Pay-Acceptance.zip).
 * Google's production review (Pay & Wallet Console) rejects plain "Google Pay"
 * text, which is what both buttons were until 2026-10-02.
 */

// TEST issues dummy tokens and needs no Business Console approval. PRODUCTION
// issues real card tokens and is approved for www.newreg.co.uk ONLY (Pay &
// Wallet Console, merchant BCR2DN5TQ2H6LBDU, Web integration Active 2026-10-06).
// Chosen at RUNTIME by hostname, not at build time: beta and www ship the same
// bundle, and no build step sets REACT_APP_* (the Jenkinsfile never builds
// React; the old "(Jenkins)" note here was never true). Host allow-list, same
// pattern as WALLET_PAY_ENABLED / APPLE_PAY_ENABLED: any other host stays TEST,
// because PRODUCTION on an unapproved domain fails for the customer.
//
// The host is assembled with join() ON PURPOSE. Whatever serves beta rewrites
// every literal "www.newreg.co.uk" in the served JS to "beta.newreg.co.uk"
// (seen 2026-10-06: the file on disk has the www literal, the bytes served on
// beta do not), which would turn this into "PRODUCTION on beta". A joined
// string is not a literal the rewrite can match. Do not "simplify" it back.
const GOOGLE_PAY_PRODUCTION_HOST = ['www', 'newreg', 'co', 'uk'].join('.');
export const GOOGLE_PAY_ENVIRONMENT: "PRODUCTION" | "TEST" =
  typeof window !== 'undefined' && window.location.hostname === GOOGLE_PAY_PRODUCTION_HOST
    ? "PRODUCTION"
    : "TEST";

// GOOGLE PAY LIVE on www for every customer, 2026-10-06 (Jon: "i want to get
// google pay live"), after the first production Google Pay sale went through
// 3-D Secure (P2XYP1791285279). Same host as the PRODUCTION environment, so the
// button is only ever shown to everyone where Google approved it. CheckoutInfo
// and CheckoutPayment both read this; elsewhere Google Pay still needs the
// ?wallet=test opt-in (WALLET_PAY_ENABLED) or beta.
export const GOOGLE_PAY_LIVE_FOR_ALL: boolean =
  typeof window !== 'undefined' && window.location.hostname === GOOGLE_PAY_PRODUCTION_HOST;

const SDK_URL = 'https://pay.google.com/gp/p/js/pay.js';

let sdkPromise: Promise<boolean> | null = null;

/** Load Google's pay.js once. Resolves true when google.payments.api exists. */
export function loadGooglePaySdk(): Promise<boolean> {
  if (sdkPromise) return sdkPromise;

  const ready = () => !!(window as any).google?.payments?.api;

  sdkPromise = new Promise<boolean>((resolve) => {
    if (typeof document === 'undefined') {
      resolve(false);
      return;
    }
    if (ready()) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve(ready());
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return sdkPromise;
}

/** A PaymentsClient in the build's environment. Call only after loadGooglePaySdk() resolves true. */
export function googlePaymentsClient(): any {
  return new (window as any).google.payments.api.PaymentsClient({ environment: GOOGLE_PAY_ENVIRONMENT });
}
